import {
  CLASSIC_DEFAULTS_TEXT,
  GRID_DEFAULTS_TEXT,
  GRID_LEFTIES_TEXT,
  STANDARD_DEFAULTS_TEXT,
  STANDARD_LEFTIES_TEXT,
} from "@/games/sc2/defaults";
import { EDENNIL_SC2_SOURCE } from "@/games/sc2/edennil-source";
import type { SC2Source } from "@/games/sc2/source";
import { catalogEntries } from "@/platform/thecore/ingest";

// The SC2 source list lives in platform because platform composes: it splices the TheCore catalog,
// the game's stock presets, and the Edennil personal setup into the one picker-facing list. Games
// supply the ingredients (the vendored texts and the SC2Source vocabulary), never the composition -
// this also keeps game.ts from loading the whole vendored seed archive at module load.

/**
 * The Blizzard stock hotkey Profiles - the game's own default presets, offered as selectable sources:
 * real game data extracted from CASC (the in-engine bases a sparse upload is a delta against). Four of
 * them are the game's own flat per-command tables (Standard, Classic, Standard for Lefties); Grid is
 * positional - its keys derive from each ability's command-card cell, so it is reconstructed per unit
 * and per Context (see defaults/PROVENANCE.md) rather than pulled from a column. Plain board-portable
 * sources (no Preset route, no locale download), placed after the TheCore presets so TheCore Right
 * stays the landing default.
 */
const STANDARD_SC2_SOURCE: SC2Source = {
  id: "blizzard-standard",
  name: "Blizzard Standard",
  text: STANDARD_DEFAULTS_TEXT,
};
const CLASSIC_SC2_SOURCE: SC2Source = {
  id: "blizzard-classic",
  name: "Blizzard Classic",
  text: CLASSIC_DEFAULTS_TEXT,
};
// the game's left-handed Standard (mouse in the left hand, hotkeys mirrored to the right hand).
const STANDARD_LEFT_SC2_SOURCE: SC2Source = {
  id: "blizzard-standard-left",
  name: "Blizzard Standard (Left)",
  text: STANDARD_LEFTIES_TEXT,
};
// the game's positional Grid preset and its left-handed variant.
const GRID_SC2_SOURCE: SC2Source = {
  id: "blizzard-grid",
  name: "Blizzard Grid",
  text: GRID_DEFAULTS_TEXT,
};
const GRID_LEFT_SC2_SOURCE: SC2Source = {
  id: "blizzard-grid-left",
  name: "Blizzard Grid (Left)",
  text: GRID_LEFTIES_TEXT,
};

/**
 * The selectable SC2 sources - the Profile-picker entries. Each vendored TheCore setup
 * is offered as canonical text and parsed through the active Keyboard Layout at render,
 * never pre-decoded. SC2_SOURCES[0] is the landing default: TheCore Right, the
 * first manifest setup. Then the five Blizzard stock presets (Standard, Classic, Standard
 * for Lefties, Grid, Grid for Lefties) and the Edennil personal setup.
 */
export const SC2_SOURCES: readonly SC2Source[] = [
  ...catalogEntries.map((e) => ({
    id: e.id,
    name: e.name,
    text: e.text,
    // carry the Plus derived-lens marker + hand so the renderer can build the remap layer
    ...(e.modRemap
      ? {
          modRemap: {
            kind: e.modRemap,
            hand: e.variant.hand === "left" ? "left" : "right",
          } as const,
        }
      : {}),
  })),
  // the game's stock presets, after the TheCore presets so TheCore Right stays the landing.
  STANDARD_SC2_SOURCE,
  CLASSIC_SC2_SOURCE,
  STANDARD_LEFT_SC2_SOURCE,
  GRID_SC2_SOURCE,
  GRID_LEFT_SC2_SOURCE,
  // the maintainer's personal setup, board-bound to ReCore; a plain source, not a
  // CatalogEntry, so it has no Preset route or locale download (its lens is pinned).
  EDENNIL_SC2_SOURCE,
];

/**
 * The id an uploaded setup carries, in BOTH selection models: the SC2 "uploaded" source and a
 * simple Game's ingested "my own" Profile use the same id as the selection that picks them, so the
 * two never drift. Canonical here (the source splice below is one user); the ActiveSetup seam
 * re-exports it for the cross-model consumers.
 */
export const UPLOADED_ID = "uploaded";

/**
 * The SC2 source list and active source for one side: the vendored sources plus an uploaded file
 * as another selectable source, with a stale selection (an upload cleared) deriving back to the
 * landing default. `portableOnly` drops board-bound (pinned) sources - the compare B-side, where a
 * pinned source must never render on an unpinned board; the stale-id fallback stays the UNFILTERED
 * SC2_SOURCES[0] (never pinned), not a re-derive from the filtered list. Called only by the
 * ActiveSetup seam (resolveActiveSetup), once per side, so A and B resolution can never drift.
 */
export function resolveSc2Sources(
  uploaded: { text: string; name: string } | null,
  selectedId: string,
  portableOnly = false,
): { sources: SC2Source[]; activeSource: SC2Source | null } {
  const base = portableOnly ? SC2_SOURCES.filter((s) => !s.pin) : [...SC2_SOURCES];
  const sources = uploaded
    ? [...base, { id: UPLOADED_ID, name: uploaded.name, text: uploaded.text }]
    : base;
  const activeSource = sources.find((s) => s.id === selectedId) ?? SC2_SOURCES[0] ?? null;
  return { sources, activeSource };
}
